'use client';

import React, { createContext, useContext, useCallback, useEffect, ReactNode, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { driver } from 'driver.js';
import type { Driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { useTutorialState, useTutorialSteps, TutorialStep } from '@/hooks/use-tutorial';
import { PRODUCT_TOUR_ENABLED } from '@/lib/constants';

const waitForElement = (selector: string, timeout = 5000): Promise<void> => {
  return new Promise((resolve) => {
    const interval = 100; // Check every 100ms
    let elapsedTime = 0;

    const timer = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        clearInterval(timer);
        resolve();
      } else {
        elapsedTime += interval;
        if (elapsedTime >= timeout) {
          clearInterval(timer);
          console.error(`Timed out waiting for element: ${selector}`);
          resolve(); // Resolve anyway to not block the tour
        }
      }
    }, interval);
  });
};

/** The step id we stash on each driver.js DriveStep, since driver.js steps have no native id. */
function getStepId(step: DriveStep | undefined): string | undefined {
  return (step?.data as { id?: string } | undefined)?.id;
}

/** Maps our own step data (see hooks/use-tutorial.ts) to driver.js's DriveStep shape. */
function toDriveSteps(steps: TutorialStep[]): DriveStep[] {
  return steps.map((step): DriveStep => {
    const showButtons: Array<'next' | 'previous' | 'close'> = ['close'];
    if (step.nextButton || step.doneButton) {
      showButtons.unshift('next');
    }

    return {
      element: step.element,
      data: { id: step.id },
      popover: {
        title: step.title,
        description: step.text,
        side: step.side,
        popoverClass: step.popoverClass,
        showButtons,
        showProgress: false,
        ...(step.nextButton && {
          nextBtnText: step.nextButton.label,
          onNextClick: (_element, _driveStep, opts) => {
            const onClick = step.nextButton?.onClick ?? ((d: Driver) => d.moveNext());
            onClick(opts.driver);
          },
        }),
        ...(step.doneButton && {
          doneBtnText: step.doneButton.label,
          onDoneClick: (_element, _driveStep, opts) => {
            step.doneButton?.onClick(opts.driver);
          },
        }),
      },
    };
  });
}

export interface TutorialContextType {
  isTutorialMode: boolean;
  advanceTutorial: () => void;
  moveBackTutorial: () => void;
  tour: Driver | null;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

interface TutorialProviderProps {
  children: ReactNode;
}

export function TutorialProvider({ children }: TutorialProviderProps) {
  const t = useTranslations('Tutorial');
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const [tour, setTour] = useState<Driver | null>(null);
  const { saveState: saveTutorialState, clearState: clearTutorialState, getState: getTutorialState } = useTutorialState();
  const steps = useTutorialSteps();

  // PRODUCT_TOUR_ENABLED gates the tour off entirely (see lib/constants.ts,
  // KB-289): neither the ?tutorial=true param nor an already-active tour
  // instance may flip the app into tutorial mode while it is false.
  const isTutorialMode = PRODUCT_TOUR_ENABLED && (searchParams.get('tutorial') === 'true' || tour?.isActive() || false);

  const exitTutorial = useCallback(() => {
    try {
      clearTutorialState();
      // Remove tutorial param from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('tutorial');
      router.replace(url.pathname + url.search);
    } catch (error) {
      console.error('Failed to exit tutorial:', error);
    }
  }, [clearTutorialState, router]);

  const advanceTutorial = useCallback(() => {
    try {
      if (tour?.isActive()) {
        // Get current step
        const currentStepId = getStepId(tour.getActiveStep());

        // Get next step
        const currentIndex = steps.findIndex((step) => step.id === currentStepId);
        const nextStep = steps[currentIndex + 1];

        if (nextStep?.id === 'credits-deducted' || nextStep?.id === 'tutorial-complete') {
          setTimeout(() => tour.moveNext(), 2000); // Delay to allow UI to update
        } else if (nextStep?.element) {
          waitForElement(nextStep.element).then(() => tour.moveNext());
        } else {
          tour.moveNext();
        }
      }
    } catch (error) {
      console.error('Failed to advance tutorial:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour]);

  const moveBackTutorial = useCallback(() => {
    try {
      if (tour?.isActive()) {
        // Get current step
        const currentStepId = getStepId(tour.getActiveStep());

        // Get previous step
        const currentIndex = steps.findIndex((step) => step.id === currentStepId);
        const prevStep = steps[currentIndex - 1];

        if (prevStep && prevStep.element) {
          waitForElement(prevStep.element).then(() => tour.movePrevious());
        } else {
          tour.movePrevious();
        }
      }
    } catch (error) {
      console.error('Failed to move back tutorial:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour]);

  const startTutorial = useCallback(async () => {
    try {
      if (tour) {
        console.log('Tour already active, cancelling and restarting');
        tour.destroy();
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // driver.js resolves Escape/close-icon/overlay-click through the same
      // onDestroyStarted hook, so a single confirm() covers all three, the
      // same way Shepherd's confirmCancel covered its cancel icon and Escape.
      const confirmExit = (driverInstance: Driver) => {
        if (window.confirm(t('exitTour'))) {
          driverInstance.destroy();
        }
      };

      // Arrow-key navigation is disabled: driver.js's default lets ArrowRight
      // call the same handler as the visible "Next" button, which would skip
      // past steps that must only advance once the app calls
      // advanceTutorial() after a real on-page action. Escape is restored
      // manually below since driver.js gates it behind the same flag.
      const newTour = driver({
        showProgress: false,
        allowKeyboardControl: false,
        // No dimmed backdrop: most steps are advanced by the visitor
        // clicking a real control elsewhere on the page (Run Workflow,
        // Create Job, ...), not the highlighted element itself, so nothing
        // on the page can be dimmed or click-blocked. See styles/tour.css
        // for the matching overlay-and-pointer-events override.
        overlayOpacity: 0,
        steps: toDriveSteps(steps),
        onDestroyStarted: (_element, _step, opts) => confirmExit(opts.driver),
        onDestroyed: () => {
          window.removeEventListener('keydown', handleEscape);
          exitTutorial();
        },
        onHighlighted: (_element, step) => {
          try {
            const stepId = getStepId(step);
            if (stepId) {
              const currentIndex = steps.findIndex((s) => s.id === stepId);
              saveTutorialState(stepId, currentIndex);
            }
          } catch (error) {
            console.error('Failed to handle tour step highlight:', error);
          }
        },
      });

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && newTour.isActive()) {
          confirmExit(newTour);
        }
      };
      window.addEventListener('keydown', handleEscape);

      // Check if we should resume from a saved state
      const savedState = getTutorialState();
      if (savedState && savedState.isActive) {
        console.log('Resuming tutorial from saved state:', savedState);
        // Try to resume from saved step
        const stepIndex = steps.findIndex((step) => step.id === savedState.currentStepId);
        if (stepIndex !== -1) {
          newTour.drive(stepIndex);
          setTour(newTour);
          return;
        }
      }

      // Start from beginning if no valid saved state
      newTour.drive();
      setTour(newTour);
      console.log('Started tutorial tour:', newTour);
    } catch (error) {
      console.error('Failed to start tutorial:', error);
    }
  }, [tour, t, exitTutorial, steps, getTutorialState, saveTutorialState]);

  // Auto-start tutorial if tutorial parameter is present or if we have saved state
  useEffect(() => {
    if (!PRODUCT_TOUR_ENABLED) {
      // The tour is disabled (see PRODUCT_TOUR_ENABLED, KB-289): clear any
      // step saved before this rollout so a visitor who was mid-tour does
      // not resume into a broken overlay next time this flips back on.
      clearTutorialState();
      return;
    }
    try {
      const savedState = getTutorialState();
      const shouldStartTutorial = !isMobile && (isTutorialMode || (savedState && savedState.isActive));

      if (shouldStartTutorial && !tour?.isActive()) {
        // Small delay to ensure the page is fully loaded
        setTimeout(() => {
          try {
            startTutorial();
          } catch (error) {
            console.error('Failed to start tutorial in timeout:', error);
          }
        }, 200);
      }
    } catch (error) {
      console.error('Failed to check tutorial auto-start conditions:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialMode]);

  const value: TutorialContextType = {
    isTutorialMode,
    advanceTutorial,
    moveBackTutorial,
    tour,
  };

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}

export function useTutorial(): TutorialContextType {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorialContext must be used within a TutorialProvider');
  }
  return context;
}
